/**
 * CORE.TYPES.TS - Mathematical Primitives & Geometric Foundation
 *
 * PURPOSE: Zero-dependency geometric types used across ALL domains
 * PRINCIPLE: If it's a number or a coordinate, it lives here
 * DEPENDENCIES: None (this is the bottom of the dependency tree)
 *
 * @version 5.0.0
 * @author Uttam Panda
 * @copyright (c) 2025 DysTest. All rights reserved.
 */
// ============================================================================
// LOGGING TYPES
// ============================================================================

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  AUDIT = 4,
  CRITICAL = 5,
}

export type LogCategory =
  | "SECURITY"
  | "PERFORMANCE"
  | "BUSINESS"
  | "TECHNICAL"
  | "COMPLIANCE"
  | "SYSTEM"
  | "USER_ACTION"
  | "API"
  | "AUDIT"
  | "VISION"
  | "GAZE"
  | "KEYSTROKE"
  | "CLINICAL";

export type LogDestination = "console" | "file" | "api" | "database";

export interface LogEntry {
  readonly timestamp: string;
  readonly level: LogLevel;
  readonly category: LogCategory;
  readonly message: string;
  readonly data?: Record<string, any> | undefined;
  readonly sessionId?: string | undefined;
  readonly userId?: string | undefined;
  readonly correlationId?: string | undefined;
  readonly source: string;
  readonly stackTrace?: string | undefined;
  readonly performanceMetrics?: PerformanceMetrics | undefined;
  readonly retryCount?: number | undefined;
  readonly id?: string | undefined;
}

// ============================================================================
// PERFORMANCE & TIMING
// ============================================================================

/**
 * Frame Rate Controller for adaptive processing
 */
export interface FrameRateController {
  shouldProcessFrame(currentTime: number): boolean;
  reset(): void;
  getCurrentFPS(): number;
}

/**
 * Comprehensive performance metrics for clinical frame timing
 */
export interface PerformanceMetrics {
  // Clinical Frame Timing (Required for calculateFrameMetrics)
  readonly fps: number;
  readonly averageFrameTime: number;
  readonly minFrameTime: number;
  readonly maxFrameTime: number;
  readonly jankCount: number;
  readonly jankPercentage: number;
  readonly frameCount: number;
  readonly duration: number;

  // System Metadata (Contextual)
  readonly memoryUsed?: number;
  readonly componentName?: string;
  readonly operationType?: string;
}

export interface LoggerConfig {
  readonly minLevel: LogLevel;
  readonly destinations: readonly LogDestination[];
  readonly enableBatching: boolean;
  readonly batchSize: number;
  readonly batchInterval: number;
  readonly enablePiiProtection: boolean;
  readonly enablePerformanceTracking: boolean;
  readonly maxRetries: number;
  readonly retryBackoffMs: number;
  readonly circuitBreakerThreshold: number;
  readonly sanitizeFields: readonly string[];
  readonly useIdleCallback: boolean;
  readonly useSendBeacon: boolean;
  readonly environment: "development" | "staging" | "production";
  readonly remoteEndpoint?: string | undefined;
  readonly maxBatchSize: number;
  readonly appVersion: string;
}

// ============================================================================
// GEOMETRIC PRIMITIVES
// ============================================================================

/**
 * 2D Point (pixel coordinates or normalized 0-1)
 */
export interface Point2D {
  readonly x: number;
  readonly y: number;
}

/**
 * 3D Point (includes depth for face mesh)
 */
export interface Point3D extends Point2D {
  readonly z: number;
}

/**
 * Vector with magnitude and direction
 */
export interface Vector2D extends Point2D {
  readonly magnitude?: number;
  readonly angle?: number; // radians
}

export interface Vector3D extends Point3D {
  readonly magnitude?: number;
  readonly theta?: number; // horizontal angle (radians)
  readonly phi?: number; // vertical angle (radians)
}

// ============================================================================
// CANONICAL LANDMARK (Replaces all "NormalizedLandmark" variants)
// ============================================================================

/**
 * THE SINGLE SOURCE OF TRUTH for facial landmark coordinates
 *
 * Used by:
 * - MediaPipe Face Mesh (477 points)
 * - Eye tracking algorithms
 * - Head pose estimation
 * - Calibration systems
 *
 * CRITICAL: All coordinates are NORMALIZED (0-1 range)
 * - x: 0 = left edge, 1 = right edge
 * - y: 0 = top edge, 1 = bottom edge
 * - z: depth (relative to face plane)
 */
export interface CanonicalLandmark {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly visibility?: number; // MediaPipe confidence (0-1)
}

// ============================================================================
// SCREEN & PIXEL COORDINATES
// ============================================================================

/**
 * Absolute pixel coordinates (for UI rendering)
 */
export interface PixelPoint {
  readonly pixelX: number;
  readonly pixelY: number;
}

/**
 * Screen/Canvas dimensions
 */
export interface ScreenDimensions {
  readonly width: number; // pixels
  readonly height: number; // pixels
}

/**
 * Bounding box (Axis-Aligned Bounding Box)
 */
export interface BoundingBox {
  readonly x: number; // top-left x
  readonly y: number; // top-left y
  readonly width: number;
  readonly height: number;
}

// ============================================================================
// ANGULAR MEASUREMENTS
// ============================================================================

/**
 * Euler angles for 3D rotation (used by head pose)
 */
export interface EulerAngles {
  readonly pitch: number; // X-axis rotation (degrees) - nodding
  readonly yaw: number; // Y-axis rotation (degrees) - shaking
  readonly roll: number; // Z-axis rotation (degrees) - tilting
}

/**
 * Spherical coordinates (alternative to Euler)
 */
export interface SphericalCoordinates {
  readonly radius: number;
  readonly theta: number; // azimuthal angle (radians)
  readonly phi: number; // polar angle (radians)
}

// ============================================================================
// STATISTICAL PRIMITIVES
// ============================================================================

/**
 * Basic statistical summary
 */
export interface StatisticalSummary {
  readonly mean: number;
  readonly stdDev: number;
  readonly median: number;
  readonly variance: number;
  readonly min: number;
  readonly max: number;
  readonly count: number;
}

/**
 * Time-series data point
 */
export interface TimeSeriesPoint<T = number> {
  readonly timestamp: number; // milliseconds since epoch
  readonly value: T;
}

// ============================================================================
// QUALITY METRICS (Domain-Agnostic)
// ============================================================================

/**
 * Generic confidence score
 */
export interface ConfidenceScore {
  readonly confidence: number; // 0-1
  readonly method?: string; // how it was calculated
}

/**
 * Quality assessment (used by calibration, tracking, etc.)
 */
export interface QualityMetrics extends ConfidenceScore {
  readonly isValid: boolean;
  readonly warnings: readonly string[];
  readonly score: number; // 0-1 overall quality
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Timestamp with ISO string
 */
export interface Timestamp {
  readonly epochMs: number;
  readonly iso: string;
}

/**
 * Range constraint
 */
export interface Range {
  readonly min: number;
  readonly max: number;
}

/**
 * Normalized range (always 0-1)
 */
export interface NormalizedRange {
  readonly min: 0;
  readonly max: 1;
}

// ============================================================================
// FILTER & SMOOTHING INTERFACES
// ============================================================================

export interface LowPassFilter {
  filter(value: number): number;
  reset(): void;
  getCurrent(): number;
}

export interface KalmanFilter {
  filter(measurement: number): number;
  reset(): void;
  getEstimate(): number;
}

export interface OneEuroFilter {
  // Note: Decoupled from LowPassFilter because it requires a timestamp
  filter(value: number, timestamp: number): number;
  reset(): void;
  getCurrent(): number;
}

export interface GazeSmoother {
  smooth(point: Point2D): Point2D;
  reset(): void;
  getCurrent(): Point2D;
}

// ============================================================================
// ADDITIONAL GEOMETRIC PRIMITIVES
// ============================================================================

export interface Circle {
  readonly center: Point2D;
  readonly radius: number;
}

export interface Line {
  readonly start: Point2D;
  readonly end: Point2D;
}

export interface Polygon {
  readonly vertices: Point2D[];
  readonly centroid: Point2D;
}

/**
 * ExtendedPoint2D adds clinical metadata to a basic coordinate.
 * Used for buffered gaze data and time-series analysis.
 */
export interface ExtendedPoint2D extends Point2D {
  readonly timestamp?: number | undefined;
  readonly confidence?: number | undefined;
}
// ============================================================================
// ERROR & VALIDATION TYPES
// ============================================================================

export interface ClinicalErrorDetails {
  readonly code: string;
  readonly message: string;
  readonly timestamp: string;
  readonly context?: Record<string, any> | undefined; // Add explicit undefined
  readonly severity: "low" | "medium" | "high";
  readonly recoveryAction?: string | undefined; // Add explicit undefined
}

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isPoint2D(obj: unknown): obj is Point2D {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "x" in obj &&
    "y" in obj &&
    typeof (obj as Point2D).x === "number" &&
    typeof (obj as Point2D).y === "number"
  );
}

export function isPoint3D(obj: unknown): obj is Point3D {
  return isPoint2D(obj) && "z" in obj && typeof (obj as Point3D).z === "number";
}

export function isCanonicalLandmark(obj: unknown): obj is CanonicalLandmark {
  return isPoint3D(obj);
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const EPSILON = 1e-6; // For floating-point comparisons

export const NORMALIZATION_RANGE: NormalizedRange = {
  min: 0,
  max: 1,
} as const;

// ============================================================================
// CLINICAL CONSTANTS
// ============================================================================

export const CLINICAL_EPSILON = {
  STANDARD: 1e-10,
  HIGH: 1e-12,
  CLINICAL: 1e-14, // For gaze tracking precision
  ANGLE: 1e-8, // For angular measurements
} as const;

export const GAZE_CONSTANTS = {
  FIXATION_DURATION_MIN: 50, // milliseconds
  FIXATION_DURATION_MAX: 10000, // milliseconds
  SACCADE_VELOCITY_MIN: 30, // degrees/second
  SACCADE_VELOCITY_MAX: 1000, // degrees/second
  PUPIL_DIAMETER_MIN: 2, // millimeters
  PUPIL_DIAMETER_MAX: 8, // millimeters
  CONFIDENCE_THRESHOLD: 0.5, // Minimum confidence for valid gaze
  SAMPLE_RATE_TARGET: 60, // Frames per second
} as const;